import { useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowLeft, RefreshCw, ArrowRight, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_STYLES = {
  create:    { color: 'border-green-500/40 bg-green-500/5 text-green-300',   icon: CheckCircle2, label: 'Create' },
  update:    { color: 'border-violet-500/40 bg-violet-500/5 text-violet-300', icon: RotateCcw, label: 'Update' },
  split:     { color: 'border-amber-500/40 bg-amber-500/5 text-amber-300',   icon: AlertTriangle, label: 'Split' },
  duplicate: { color: 'border-zinc-500/40 bg-zinc-500/5 text-zinc-300',      icon: XCircle, label: 'Duplicate' },
  ambiguous: { color: 'border-blue-500/40 bg-blue-500/5 text-blue-300',      icon: AlertTriangle, label: 'Pick staff' },
  not_found: { color: 'border-red-500/40 bg-red-500/5 text-red-300',         icon: XCircle, label: 'Not found' },
  no_event:  { color: 'border-orange-500/40 bg-orange-500/5 text-orange-300', icon: XCircle, label: 'No event' },
  invalid:   { color: 'border-red-500/40 bg-red-500/5 text-red-300',         icon: XCircle, label: 'Invalid' },
  skip:      { color: 'border-zinc-500/40 bg-zinc-500/5 text-zinc-300',      icon: XCircle, label: 'Skipped' },
};

// Human-readable labels for the 4 canonical roles + fallback bucket used by
// the capacity report and the per-row role selector.
const ROLE_LABELS = {
  sia_door_supervisor: 'SIA Door Supervisor',
  cso_steward: 'CSO Steward',
  cleaner: 'Cleaner',
  supervisor: 'Supervisor',
  unassigned: 'Unassigned',
};

const ROLE_STATUS_STYLES = {
  exact_match: { color: 'border-green-500/40 bg-green-500/5 text-green-300', label: 'Exact Match' },
  shortage:    { color: 'border-amber-500/40 bg-amber-500/10 text-amber-300', label: 'Staff Shortage' },
  overfill:    { color: 'border-red-500/40 bg-red-500/10 text-red-300',      label: 'Staff Overfill' },
};

export default function ImportExcelDialog({ open, onOpenChange, onImported, targetGroup = null, allStaff = [] }) {
  const [stage, setStage] = useState('upload'); // upload | preview | report
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [report, setReport] = useState(null);
  const [markMissingPending, setMarkMissingPending] = useState(true);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setStage('upload');
    setFile(null);
    setPreviewData(null);
    setReport(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback((nextOpen) => {
    if (!nextOpen) reset();
    onOpenChange?.(nextOpen);
  }, [onOpenChange, reset]);

  const acceptFile = (f) => {
    if (!f) return;
    const ok = /\.xlsx?$/i.test(f.name);
    if (!ok) {
      toast.error('Please choose an Excel file (.xlsx or .xls)');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File is too large (max 10MB)');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    acceptFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (targetGroup?.openShiftIds?.length) {
        form.append('target_open_shift_ids', targetGroup.openShiftIds.join(','));
      }
      if (targetGroup?.label) {
        form.append('target_event_group', targetGroup.label);
      }
      const res = await axios.post(`${API}/admin/shifts/import-excel/preview`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Apply the backend's per-role capacity decisions: rows marked as
      // overfill are pre-unticked so admin has to explicitly confirm them,
      // matching the "no accidental over-allocation" requirement.
      const withDefaults = {
        ...res.data,
        rows: (res.data.rows || []).map((r) => ({
          ...r,
          _included: r._default_included !== false,
        })),
      };
      setPreviewData(withDefaults);
      setStage('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to read the file');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (rowNo, patch) => {
    setPreviewData((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (r.row_no === rowNo ? { ...r, ...patch } : r)),
    }));
  };

  const handleConfirm = async () => {
    if (!previewData) return;
    setLoading(true);
    try {
      // Recompute overfill live before sending — mirrors the logic used in
      // renderPreview so admin's role reassignments are honoured.
      const perDateRequired = previewData.per_date_required || {};
      const byDateRole = {};
      previewData.rows.forEach((r) => {
        const importable = (
          r.action === 'create' || r.action === 'split' ||
          ((r.action === 'ambiguous' || r.action === 'not_found') && !!r.chosen_user_id)
        );
        if (!importable || !r.date) return;
        const role = r.derived_role || 'unassigned';
        (byDateRole[`${r.date}|${role}`] = byDateRole[`${r.date}|${role}`] || []).push(r);
      });
      const overfillIds = new Set();
      Object.entries(byDateRole).forEach(([k, list]) => {
        const [date, role] = k.split('|');
        const required = perDateRequired[date]?.[role] || 0;
        if (list.length > required) {
          list.slice().sort((a, b) => a.row_no - b.row_no).slice(required).forEach((r) => overfillIds.add(r.row_no));
        }
      });
      // Only send rows that admin has ticked. Unticked rows are dropped
      // entirely so the backend treats them as skipped.
      const rows = previewData.rows
        .filter((r) => r._included !== false)
        .map((r) => ({
          row_no: r.row_no,
          raw_name: r.raw_name,
          date: r.date,
          start_time: r.start_time,
          end_time: r.end_time,
          chosen_user_id: r.chosen_user_id || '',
          target_open_shift_id: r.target_open_shift_id || '',
          existing_shift_id: r.existing_shift_id || '',
          // Explicit admin confirmation to exceed the (date, role) capacity.
          admin_confirmed_overfill: overfillIds.has(r.row_no),
          action: r.action,
        }));
      const res = await axios.post(`${API}/admin/shifts/import-excel/confirm`, {
        rows,
        mark_missing_pending: !!(targetGroup?.openShiftIds?.length) && markMissingPending,
        group_open_shift_ids: targetGroup?.openShiftIds || [],
      });
      setReport(res.data);
      setStage('report');
      const total = (res.data.imported || 0) + (res.data.split_created || 0) + (res.data.updated || 0);
      toast.success(`Imported / updated ${total} shift(s)`);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  // A row is "actionable" — admin can choose whether to include it. Read-only
  // statuses (duplicate / no_event / invalid) stay un-checkable because the
  // backend can't act on them anyway.
  const isActionable = (r) =>
    ['create', 'update', 'split'].includes(r.action) ||
    ((r.action === 'ambiguous' || r.action === 'not_found') && !!r.chosen_user_id);

  const toggleAll = (checked) => {
    setPreviewData((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (isActionable(r) ? { ...r, _included: checked } : r)),
    }));
  };

  // Pre-built staff dropdown options for "not_found" manual picking
  const staffOptions = useMemo(
    () => (allStaff || [])
      .filter((u) => u?.id && u?.name)
      .map((u) => ({ id: u.id, name: u.name, role: u.role, employee_code: u.employee_code }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allStaff],
  );

  // -------- UPLOAD STAGE --------
  const renderUpload = () => (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="import-excel-dropzone"
        className={`border-2 border-dashed rounded-md p-12 text-center cursor-pointer transition-colors ${
          dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
        }`}
      >
        <UploadCloud className="w-14 h-14 mx-auto mb-3 text-muted-foreground" />
        <p className="text-base font-semibold mb-1">Drag &amp; drop your Excel file here</p>
        <p className="text-sm text-muted-foreground">or click to browse — .xlsx / .xls, up to 10MB</p>
        {file && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-sm">
            <FileSpreadsheet className="w-4 h-4 text-green-400" />
            <span className="text-sm">{file.name}</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0])}
          data-testid="import-excel-file-input"
        />
      </div>
      <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-sm p-3">
        <p className="font-semibold mb-1">Expected columns (header detected automatically):</p>
        <p>• <strong>Date</strong> — when the shift happens (e.g. 18/06/2026)</p>
        <p>• <strong>Staff Name</strong> — full name; first/last in any order, case-insensitive</p>
        <p>• <strong>Shift On</strong> — start time (HH:MM)</p>
        <p>• <strong>Shift Off</strong> — end time (HH:MM, can be next day for night shifts)</p>
        <p className="mt-2">Other columns (position, sector, comments…) are ignored.</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => handleClose(false)} data-testid="import-excel-cancel-btn">Cancel</Button>
        <Button
          onClick={handlePreview}
          disabled={!file || loading}
          className="bg-primary text-primary-foreground hover:bg-yellow-300"
          data-testid="import-excel-preview-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
          Read &amp; Preview
        </Button>
      </div>
    </div>
  );

  // -------- PREVIEW STAGE --------
  const renderPreview = () => {
    const s = previewData?.summary || {};
    const rows = previewData?.rows || [];
    const scope = previewData?.scope_info || {};
    const perDateRequired = previewData?.per_date_required || {};
    const actionableRows = rows.filter(isActionable);
    // Live recompute of overfill markers + capacity report so admin edits to
    // the per-row Role selector reflect immediately. We rely on `is_overfill`
    // for the row-level red ring and the "Confirm" button counters; the
    // capacityReport array is rebuilt bottom-up from the current rows.
    const liveRows = (() => {
      const byDateRole = {};
      rows.forEach((r) => {
        const importable = (
          r.action === 'create' || r.action === 'split' ||
          ((r.action === 'ambiguous' || r.action === 'not_found') && !!r.chosen_user_id)
        );
        if (!importable || !r.date) return;
        const role = r.derived_role || 'unassigned';
        (byDateRole[`${r.date}|${role}`] = byDateRole[`${r.date}|${role}`] || []).push(r);
      });
      const overfillIds = new Set();
      Object.entries(byDateRole).forEach(([k, list]) => {
        const [date, role] = k.split('|');
        const required = perDateRequired[date]?.[role] || 0;
        if (list.length > required) {
          list.slice().sort((a, b) => a.row_no - b.row_no).slice(required).forEach((r) => overfillIds.add(r.row_no));
        }
      });
      return rows.map((r) => ({ ...r, is_overfill: overfillIds.has(r.row_no) }));
    })();
    // Build the coloured per-day per-role cards from the live rows.
    const capacityReport = [];
    const allRoles = ['sia_door_supervisor', 'cso_steward', 'cleaner', 'supervisor', 'unassigned'];
    const dates = Object.keys(perDateRequired).sort();
    dates.forEach((date) => {
      allRoles.forEach((role) => {
        const found = liveRows.filter((r) => r.date === date && (r.derived_role || 'unassigned') === role
          && (r.action === 'create' || r.action === 'split' ||
              ((r.action === 'ambiguous' || r.action === 'not_found') && !!r.chosen_user_id))
        ).length;
        const required = perDateRequired[date]?.[role] || 0;
        if (found === 0 && required === 0) return;
        let status = 'exact_match';
        if (found < required) status = 'shortage';
        else if (found > required) status = 'overfill';
        capacityReport.push({ date, role, required, found, status });
      });
    });
    const includedRows = actionableRows.filter((r) => r._included !== false);
    const overfillIncluded = includedRows.filter((r) => liveRows.find((x) => x.row_no === r.row_no)?.is_overfill).length;
    const allChecked = actionableRows.length > 0 && includedRows.length === actionableRows.length;
    const someChecked = includedRows.length > 0 && !allChecked;
    // Group the capacity report by date for a compact per-day card layout.
    const capacityByDate = capacityReport.reduce((acc, entry) => {
      (acc[entry.date] = acc[entry.date] || []).push(entry);
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
          <SummaryPill label="To create" value={s.create} color="green" />
          <SummaryPill label="Update" value={s.update} color="violet" />
          <SummaryPill label="Split" value={s.split} color="amber" />
          <SummaryPill label="Duplicates" value={s.duplicate} color="zinc" />
          <SummaryPill label="Pick staff" value={s.ambiguous} color="blue" />
          <SummaryPill label="Not found" value={s.not_found} color="red" />
          <SummaryPill label="No event" value={s.no_event} color="orange" />
        </div>

        {scope.scoped && scope.rows_outside_event_window > 0 && (
          <div className="border border-orange-500/40 bg-orange-500/5 rounded-sm p-3 text-sm text-orange-300" data-testid="import-scope-warning">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>{scope.rows_outside_event_window}</strong> row(s) have dates outside this event
            {scope.event_dates?.length ? (
              <> ({scope.event_dates[0]} → {scope.event_dates[scope.event_dates.length - 1]})</>
            ) : null}
            . These will be skipped — switch to the global "Import Excel" button on top of the page if you want to import for multiple events at once.
          </div>
        )}

        {/* Per-day, per-role capacity validation. Coloured cards let admin see
            at a glance which (date, role) buckets are in shortage or overfill
            before importing. Overfill rows are auto-unticked below. */}
        {Object.keys(capacityByDate).length > 0 && (
          <div className="space-y-2" data-testid="import-capacity-report">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Staffing check — per day &amp; per role
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(capacityByDate).map(([date, entries]) => (
                <div
                  key={date}
                  className="border border-border bg-muted/30 rounded-sm p-3 space-y-1.5"
                  data-testid={`import-capacity-date-${date}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold">{date}</span>
                  </div>
                  {entries.map((entry) => {
                    const style = ROLE_STATUS_STYLES[entry.status] || ROLE_STATUS_STYLES.exact_match;
                    return (
                      <div
                        key={`${date}-${entry.role}`}
                        className={`flex items-center justify-between text-xs px-2 py-1 rounded-sm border ${style.color}`}
                        data-testid={`import-capacity-${date}-${entry.role}`}
                      >
                        <span className="font-medium">{ROLE_LABELS[entry.role] || entry.role}</span>
                        <span className="flex items-center gap-2">
                          <span className="font-mono opacity-80">
                            Req {entry.required} / Excel {entry.found}
                          </span>
                          <strong className="uppercase tracking-wider text-[10px]">{style.label}</strong>
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {overfillIncluded > 0 && (
              <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-2 text-xs text-red-300" data-testid="import-overfill-warning">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                <strong>{overfillIncluded}</strong> overfill row(s) are ticked and WILL be imported — this exceeds the planned per-role capacity. Untick to leave them out.
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 border border-border rounded-sm p-2">
          <span>
            <strong className="text-foreground">{includedRows.length}</strong> of {actionableRows.length} actionable rows selected for import. Tick / untick to control individual rows.
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => toggleAll(true)} data-testid="import-check-all">Check all</Button>
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => toggleAll(false)} data-testid="import-uncheck-all">Uncheck all</Button>
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto border border-border rounded-sm">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background border-b border-border">
              <tr className="text-left">
                <th className="px-2 py-2 font-semibold w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="w-4 h-4 cursor-pointer"
                    data-testid="import-header-checkbox"
                  />
                </th>
                <th className="px-3 py-2 font-semibold">#</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold">Start</th>
                <th className="px-3 py-2 font-semibold">End</th>
                <th className="px-3 py-2 font-semibold">Event</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {liveRows.map((r) => {
                const style = ACTION_STYLES[r.action] || ACTION_STYLES.invalid;
                const Icon = style.icon;
                const showPicker = (r.action === 'ambiguous' || r.action === 'not_found');
                const pickerOptions = r.action === 'ambiguous'
                  ? (r.candidates || []).map((c) => ({ id: c.id, name: c.name, role: c.role, employee_code: c.employee_code }))
                  : staffOptions;
                const checkable = isActionable(r);
                const isChecked = checkable && r._included !== false;
                return (
                  <tr
                    key={r.row_no}
                    className={`border-b border-border/40 ${style.color} ${checkable && !isChecked ? 'opacity-50' : ''} ${r.is_overfill ? 'ring-1 ring-red-500/50' : ''}`}
                    data-testid={`import-row-${r.row_no}`}
                    data-overfill={r.is_overfill ? 'true' : 'false'}
                  >
                    <td className="px-2 py-2 text-center">
                      {checkable ? (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => updateRow(r.row_no, { _included: e.target.checked })}
                          className="w-4 h-4 cursor-pointer"
                          data-testid={`import-row-${r.row_no}-checkbox`}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.row_no}</td>
                    <td className="px-3 py-2">
                      {showPicker ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs italic">{r.raw_name}</span>
                          <Select
                            value={r.chosen_user_id}
                            onValueChange={(v) => {
                              const cand = pickerOptions.find((c) => c.id === v);
                              updateRow(r.row_no, {
                                chosen_user_id: v,
                                chosen_user_name: cand?.name || '',
                                // Promote to 'create' so backend processes it
                                action: r.action === 'not_found' ? 'not_found' : 'ambiguous',
                              });
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs" data-testid={`import-row-${r.row_no}-picker`}>
                              <SelectValue placeholder={r.action === 'not_found' ? 'Pick manually…' : 'Pick one…'} />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {pickerOptions.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name} {c.employee_code ? `(${c.employee_code})` : ''}{c.role === 'supervisor' ? ' — sup' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <span>{r.chosen_user_name || r.raw_name || <span className="italic text-muted-foreground">(blank)</span>}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* Per-row role selector — admin can re-tag any row so
                          Cleaner / Supervisor rows (which we cannot detect
                          automatically) are counted against the right bucket
                          in the capacity report. */}
                      <Select
                        value={r.derived_role || 'unassigned'}
                        onValueChange={(v) => updateRow(r.row_no, { derived_role: v === 'unassigned' ? '' : v })}
                      >
                        <SelectTrigger className="h-7 text-[11px] w-[140px]" data-testid={`import-row-${r.row_no}-role`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sia_door_supervisor">SIA Door Sup.</SelectItem>
                          <SelectItem value="cso_steward">CSO Steward</SelectItem>
                          <SelectItem value="cleaner">Cleaner</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">{r.date || '—'}</td>
                    <td className="px-3 py-2">
                      {r.action === 'update' && r.existing_start_time !== r.start_time ? (
                        <span className="flex items-center gap-1 text-xs">
                          <span className="line-through opacity-60">{r.existing_start_time || 'PENDING'}</span>
                          <ArrowRight className="w-3 h-3" />
                          <strong>{r.start_time}</strong>
                        </span>
                      ) : (
                        r.start_time || '—'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.action === 'update' && r.existing_end_time !== r.end_time ? (
                        <span className="flex items-center gap-1 text-xs">
                          <span className="line-through opacity-60">{r.existing_end_time || 'PENDING'}</span>
                          <ArrowRight className="w-3 h-3" />
                          <strong>{r.end_time}</strong>
                        </span>
                      ) : (
                        r.end_time || '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.target_event_label || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" />
                        <span className="text-xs font-semibold">{style.label}</span>
                      </div>
                      <span className="text-xs opacity-70">{r.note}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center gap-2 pt-2">
          <Button variant="outline" onClick={() => { setStage('upload'); setPreviewData(null); }} data-testid="import-excel-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            {targetGroup?.openShiftIds?.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-muted-foreground" title="Staff already on this event whose (name, day) is not in this file will have their times cleared to Pending / To be confirmed. Nothing is deleted.">
                <input
                  type="checkbox"
                  checked={markMissingPending}
                  onChange={(e) => setMarkMissingPending(e.target.checked)}
                  data-testid="import-mark-missing-pending"
                  className="w-4 h-4 rounded-sm accent-[#FACC15]"
                />
                Set staff missing from this file to Pending
              </label>
            )}
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={loading || includedRows.length === 0}
              className="bg-primary text-primary-foreground hover:bg-yellow-300"
              data-testid="import-excel-confirm-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirm Import ({includedRows.length})
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // -------- REPORT STAGE --------
  const renderReport = () => {
    const r = report || {};
    return (
      <div className="space-y-4">
        <div className="text-center py-4">
          <CheckCircle2 className="w-14 h-14 mx-auto text-green-400 mb-2" />
          <h3 className="text-lg font-semibold">Import finished</h3>
          <p className="text-sm text-muted-foreground">Here's what happened:</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <ReportCell label="Imported" value={r.imported} color="green" />
          <ReportCell label="Updated" value={r.updated} color="violet" />
          <ReportCell label="Split created" value={r.split_created} color="amber" />
          <ReportCell label="Duplicates skipped" value={r.skipped_duplicate} color="zinc" />
          <ReportCell label="Name not found" value={r.skipped_not_found} color="red" />
          <ReportCell label="Ambiguous skipped" value={r.skipped_ambiguous} color="blue" />
          <ReportCell label="No event match" value={r.skipped_no_event} color="orange" />
          <ReportCell label="Invalid rows" value={r.skipped_invalid} color="red" />
          <ReportCell label="Overfill skipped" value={r.skipped_overfill} color="red" />
          <ReportCell label="Set to Pending" value={r.marked_pending} color="amber" />
          <ReportCell label="Emails sent" value={r.emails_sent} color="green" />
        </div>
        {(r.errors || []).length > 0 && (
          <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-3 max-h-40 overflow-y-auto">
            <p className="text-sm font-semibold text-red-300 mb-1">Errors:</p>
            <ul className="text-xs space-y-1">
              {r.errors.map((e, i) => (
                <li key={i}>Row {e.row_no}: {e.error}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset} data-testid="import-excel-import-another-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Import another
          </Button>
          <Button onClick={() => handleClose(false)} className="bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="import-excel-done-btn">
            Done
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-barlow tracking-wider uppercase">
            <FileSpreadsheet className="w-5 h-5 text-green-400" />
            Import Shifts from Excel
          </DialogTitle>
          <DialogDescription>
            {targetGroup?.displayName && (
              <span className="block text-amber-300 mb-1">
                Target event: <strong>{targetGroup.displayName}</strong>
              </span>
            )}
            {stage === 'upload' && (
              targetGroup
                ? 'Only rows whose date matches this event will be imported.'
                : 'Upload a staff rota and the system will create shifts automatically.'
            )}
            {stage === 'preview' && `Review ${previewData?.total_rows || 0} row(s) before importing.`}
            {stage === 'report' && 'Final result of the import.'}
          </DialogDescription>
        </DialogHeader>
        {stage === 'upload' && renderUpload()}
        {stage === 'preview' && renderPreview()}
        {stage === 'report' && renderReport()}
      </DialogContent>
    </Dialog>
  );
}

function SummaryPill({ label, value, color }) {
  const colors = {
    green: 'border-green-500/40 bg-green-500/10 text-green-300',
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    zinc:  'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
    blue:  'border-blue-500/40 bg-blue-500/10 text-blue-300',
    red:   'border-red-500/40 bg-red-500/10 text-red-300',
    orange:'border-orange-500/40 bg-orange-500/10 text-orange-300',
    violet:'border-violet-500/40 bg-violet-500/10 text-violet-300',
  };
  return (
    <div className={`border rounded-sm p-2 text-center ${colors[color]}`}>
      <div className="text-2xl font-bold">{value || 0}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function ReportCell({ label, value, color }) {
  const colors = {
    green: 'border-green-500/40 bg-green-500/10 text-green-300',
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    zinc:  'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
    blue:  'border-blue-500/40 bg-blue-500/10 text-blue-300',
    red:   'border-red-500/40 bg-red-500/10 text-red-300',
    orange:'border-orange-500/40 bg-orange-500/10 text-orange-300',
    violet:'border-violet-500/40 bg-violet-500/10 text-violet-300',
  };
  return (
    <div className={`border rounded-sm p-3 text-center ${colors[color]}`}>
      <div className="text-2xl font-bold">{value || 0}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
